/**
 * =============================================================================
 * USER SELECTORS — Phase 4–5 migration invariants (LOAD-BEARING)
 * =============================================================================
 *
 * The auth/profile slice migration is currently in progress (Phase 4B
 * completed; Phase 5 consumer migration starts after Phase 6 ownership
 * inversion). The invariants below govern every selector and consumer in
 * this module until that migration completes. Violating any one of them
 * surfaces as redirect loops, silent identity divergence, or over-rendering
 * regressions.
 *
 * INVARIANT 1 — Slices are eventually-consistent replicas, NOT synchronized.
 *   state.auth and state.profile.data are populated by separate dispatches
 *   from separate code paths (USER_LOGIN_SUCCESS / AUTH_LOGIN_SUCCESS;
 *   USER_LOGIN_SUCCESS / AUTH_IDENTITY_PATCH; PROFILE_HYDRATION_SUCCESS /
 *   AUTH_IDENTITY_PATCH). They MAY transiently disagree within a single
 *   thunk's tick. Consumers MUST NOT cross-read the same semantic field
 *   from both slices and expect equality.
 *
 * INVARIANT 2 — Cross-slice equality assumptions are forbidden.
 *   Do NOT write code like:
 *       state.userLogin.userInfo.email === state.auth.email
 *   Pick ONE source per consumer; that source stays consistent through
 *   Phase 6 ownership inversion (where legacy becomes a derived projection
 *   of the new canonical slices).
 *
 * INVARIANT 3 — Bootstrap reconciliation is low-frequency only.
 *   AUTH_IDENTITY_PATCH from bootstrapApp fires AT MOST ONCE per app mount.
 *   bootstrapApp must NEVER be turned into a continuous polling auth-sync
 *   loop — that would change reconciliation cadence and require new race
 *   analysis (cross-tab refresh storms, AUTH_IDENTITY_PATCH vs concurrent
 *   PROFILE_UPDATE_SUCCESS in Phase 4C, etc.).
 *
 * INVARIANT 4 — Legacy userLogin.userInfo remains AUTHORITATIVE through Phase 5.
 *   state.auth and state.profile are SECONDARY REPLICAS until Phase 6
 *   inverts ownership. Persistence (localStorage), thunk write paths
 *   (mergeUserInfo + persistUserInfo), the WS module, and the axios
 *   request interceptor continue reading from / writing through the
 *   legacy contract. Any "fix" that reads from auth/profile in those
 *   paths before Phase 6 is a bug, not a migration.
 *
 * INVARIANT 5 — Phase 5 PRs are incremental (one consumer group per PR).
 *   Each consumer migration PR moves ONE risk-group at a time. Bundles
 *   are forbidden — they conflate failure modes and resist rollback.
 *   See migration plan for sequencing.
 *
 * INVARIANT 6 — Ref-stability is mandatory for selectors used in effect deps.
 *   Any selector consumed in a useEffect dependency array MUST either
 *   return a primitive OR be createSelector-memoized. Non-memoized
 *   object-returning selectors trigger over-firing — a regression vs
 *   today's legacy reads. selectIdentity is memoized for this reason.
 *
 * INVARIANT 7 — Selectors abstract OWNERSHIP LOCATION, not SYNCHRONIZATION.
 *   These selectors hide WHERE state lives (state.userLogin.userInfo
 *   today, state.auth/state.profile.data after Phase 6). They DO NOT
 *   guarantee that consumers reading different slices via different
 *   selectors see mutually-consistent data within the same render tick.
 *   Eventually-consistency caveats from Invariant 1 STILL APPLY after
 *   Phase 6 selector swaps — during any future migration window where
 *   dual-write paths or projection lag exists, two selectors reading
 *   "the same thing from different places" can disagree for a tick.
 *   Consumers must continue to pick ONE selector per semantic concern.
 *
 * -----------------------------------------------------------------------------
 * ⚠ PRE-PHASE-6 MIGRATION WARNING — effect-cadence parity audit required.
 *
 *   Before swapping selectAccessToken's internal read from
 *   state.userLogin.userInfo.access (legacy) to state.auth.access (new),
 *   an explicit cadence audit is REQUIRED to verify that:
 *
 *     N(legacy access write events) === N(auth.access write events)
 *
 *   over every flow that today produces an access-token mutation
 *   (login, refresh, logout, login-failure-after-B2-fix). Any divergence
 *   in dispatch count or timing between the two writers will surface as
 *   altered App.js WS lifecycle effect cadence — potentially:
 *
 *     - extra wsManager.connect/disconnect cycles per session event
 *     - reconnect storms during refresh storms
 *     - missed disconnect on logout-equivalent flow
 *     - effect re-firing on previously-stable refresh attempts
 *
 *   Today's writers (legacy):
 *     login        → USER_LOGIN_SUCCESS (sets userInfo.access)
 *     googleLogin  → USER_LOGIN_SUCCESS
 *     register     → USER_LOGIN_SUCCESS
 *     refresh      → USER_TOKEN_REFRESH (sets userInfo.access)
 *     logout       → USER_LOGOUT (clears slice)
 *
 *   Today's writers (auth slice replica):
 *     login        → AUTH_LOGIN_SUCCESS
 *     googleLogin  → AUTH_LOGIN_SUCCESS
 *     register     → AUTH_LOGIN_SUCCESS
 *     refresh      → AUTH_TOKEN_REFRESH_SUCCESS
 *     logout       → AUTH_LOGOUT
 *
 *   Cadence parity is currently 1:1 by Phase 4A construction, but Phase 6
 *   ownership inversion may rewrite the writer set. Re-verify before flip.
 * -----------------------------------------------------------------------------
 *
 * -----------------------------------------------------------------------------
 * AUTH-SIGNAL CONTRACT (separate from the migration invariants):
 *
 *   "Authenticated" for UI-gating purposes is determined SOLELY by the
 *   presence of an access token in the canonical token store. Identity
 *   presence (userId, email, publicId, isEmailVerified) is NEVER an auth
 *   signal — see selectIsAuthed for the enforcement. A user with identity
 *   hydrated from a stale localStorage blob but no access token is NOT
 *   logged in for any UI-gating purpose.
 *
 *   selectIsAuthed represents the PRESENCE OF AN AUTH-SESSION CANDIDATE.
 *   It is NOT:
 *     - cryptographic validity of the token
 *     - session validity per the backend
 *     - permission to perform any specific action
 *
 *   ⚠ MUST NEVER be used for authorization or permission decisions.
 *   Authorization is a server-side responsibility; the only frontend
 *   "authorization" check is "should I render the authed shell vs the
 *   login screen". Anything stricter (e.g. "can this user write to
 *   resource X") MUST go through a server call and treat 401/403 as
 *   the source of truth — selectIsAuthed must NOT be the gate for
 *   such checks because it returns true even for expired tokens.
 * =============================================================================
 */

import { createSelector } from '@reduxjs/toolkit';

const isBlank = (value) => !value || !String(value).trim();

export const isProfileIncomplete = (userInfo) => {
  if (!userInfo) return false;
  return (
    isBlank(userInfo.first_name) ||
    isBlank(userInfo.last_name) ||
    isBlank(userInfo.handle)
  );
};

export const selectUserInfo = (state) => state.userLogin?.userInfo || null;

// =============================================================================
// New slice selectors (Phase 1+ scaffolding; populated lifecycle in Phase 2).
// Pure passthroughs — no compat layer.
// =============================================================================

export const selectAuth = (state) => state.auth;

export const selectProfile = (state) => state.profile;

export const selectBootstrap = (state) => state.bootstrap;

// =============================================================================
// TEMPORARY COMPATIBILITY SELECTORS
// -----------------------------------------------------------------------------
// Source-of-truth changes in Phase 6: each selector below currently reads the
// LEGACY state.userLogin.userInfo blob and will be swapped to read from the
// new canonical slices (state.auth, state.profile.data) without changing the
// returned shape. Consumers that adopt these selectors during Phase 5 will
// migrate transparently when Phase 6 lands.
//
// DO NOT add new behaviour here. DO NOT have consumers reach past these
// selectors into state.userLogin.userInfo directly — that defeats the purpose.
// =============================================================================

// TEMPORARY COMPATIBILITY SELECTOR — see header. Phase 6: compose auth + profile.data.
export const selectCurrentUser = (state) => state.userLogin?.userInfo || null;

/**
 * selectAccessToken — TEMPORARY COMPATIBILITY SELECTOR (see file header).
 *
 * Phase 4–5: reads state.userLogin.userInfo.access (legacy canonical store).
 * Phase 6: swaps internal read to state.auth.access. Public contract below
 * is invariant across that swap.
 *
 * PURITY INVARIANTS (load-bearing — load-time consumers depend on these):
 *
 *   * Pure raw token projection.
 *     Returns the exact string written by the auth writer (login/refresh
 *     thunks today; AUTH_LOGIN_SUCCESS / AUTH_TOKEN_REFRESH_SUCCESS in
 *     auth slice as a parallel replica). NO transformation.
 *
 *   * Identity-preserving (===).
 *     Two calls that observe the same underlying token string MUST return
 *     the SAME string reference (or, for primitives, the same value). No
 *     copy, no .toString(), no template-literal wrapping. App.js WS
 *     lifecycle effects depend on token-string === comparison in their
 *     dependency arrays — a transformation that produced a new string
 *     each call would fire effects on every dispatch and create reconnect
 *     storms.
 *
 *   * Non-normalizing.
 *     No trimming, no whitespace collapse, no case folding. Server emits
 *     a JWT; we surface it byte-for-byte. Anything else is a contract
 *     violation regardless of how "harmless" it looks.
 *
 *   * Non-transforming.
 *     No JWT decoding, no expiry inspection, no validation, no header
 *     stripping. This selector reports PRESENCE-AND-VALUE of the access
 *     token, period. Validation happens at the axios 401 boundary and
 *     the R0 refresh coordinator. The selector is a UI/effect-input
 *     primitive, not a security primitive.
 *
 *   * Returns null (not undefined) when absent.
 *     Falsy normalisation to a single sentinel keeps `Boolean(token)`
 *     consistent, makes useSelector primitive-equality comparisons
 *     deterministic across slice-shape variations.
 *
 * Any change that violates these invariants (e.g. "let's strip the Bearer
 * prefix here", "let's return decoded payload sub instead", "let's null out
 * if expired") must be made via a NEW selector with a different name, NEVER
 * by modifying selectAccessToken.
 */
export const selectAccessToken = (state) =>
  state.userLogin?.userInfo?.access || null;

/**
 * selectIsAuthed — boolean UI gate for "show authenticated UI".
 *
 * ⚠ AUTHORIZATION WARNING:
 *   This selector represents the PRESENCE OF AN AUTH-SESSION CANDIDATE,
 *   not session validity. It returns true for expired tokens, malformed
 *   tokens, server-revoked tokens — anything non-empty in the access
 *   field reads as "authed".
 *
 *   MUST NEVER be used for authorization or permission decisions.
 *   Use it ONLY for "should I render authed UI shell vs login screen".
 *   Any stricter gate (resource access, capability checks, anti-CSRF
 *   intent) must go through a server call and react to 401/403.
 *
 * SEMANTIC CONTRACT (load-bearing — see file header AUTH-SIGNAL CONTRACT):
 *
 *   - Returns TRUE if and only if a non-empty access token is present in
 *     the canonical token store (today: state.userLogin.userInfo.access;
 *     Phase 6: state.auth.access — swap is transparent because this
 *     selector composes through selectAccessToken).
 *
 *   - Returns FALSE in every other case: missing slice, missing userInfo,
 *     missing/null/empty access string.
 *
 *   - DOES NOT consider:
 *       * identity presence (userId / email / publicId / isEmailVerified)
 *       * is_email_verified boolean — verification is orthogonal to auth
 *       * profile data hydration state
 *       * server-side token validity (this selector is a UI gate, not a
 *         security check; an expired token still reads as "authed" from
 *         this selector and the axios 401 interceptor / refresh
 *         coordinator handle the recovery)
 *
 *   - DOES NOT consume identity selectors. Identity-presence-without-token
 *     would be a corrupt state (e.g. someone manually wiping access from
 *     localStorage without clearing the rest); selectIsAuthed correctly
 *     reports FALSE in that case. Composing identity into the auth signal
 *     would mask the corruption and let UI behave as if logged in.
 *
 *   - Returns a STABLE PRIMITIVE (boolean). Safe in useEffect deps.
 *
 * Intended use sites (Phase 5 migration targets — NOT migrated yet):
 *   Navbar.js          — `Boolean(state.userLogin?.userInfo?.access)` → selectIsAuthed
 *   ChatLayout.js      — same
 *   App.js (WS effects) — uses selectAccessToken directly (needs the token,
 *                         not the boolean); selectIsAuthed not applicable
 */
export const selectIsAuthed = (state) => Boolean(selectAccessToken(state));

// TEMPORARY COMPATIBILITY SELECTOR — see header. Phase 6: state.auth.
//
// Memoized via createSelector so the returned identity object keeps a stable
// reference across unrelated mutations of state.userLogin.userInfo (e.g. a
// token refresh that mints a new userInfo blob but leaves identity fields
// untouched). Without this, every refresh would force every identity-reading
// component to re-render and every identity-keyed effect to re-fire.
//
// Field-level input selectors (rather than userInfo as a single input) are
// what makes the cache survive userInfo ref churn — recompute happens only
// when one of the four identity primitives actually changes value.
export const selectIdentity = createSelector(
  [
    (state) => state.userLogin?.userInfo?.id,
    (state) => state.userLogin?.userInfo?.public_id,
    (state) => state.userLogin?.userInfo?.email,
    (state) => state.userLogin?.userInfo?.is_email_verified,
  ],
  (id, publicId, email, isEmailVerified) => {
    if (id == null && publicId == null && email == null) return null;
    return {
      userId: id ?? null,
      publicId: publicId ?? null,
      email: email ?? null,
      isEmailVerified: isEmailVerified ?? false,
    };
  }
);

/**
 * selectFollowGraph — TEMPORARY COMPATIBILITY SELECTOR (see file header).
 *
 * Phase 4–5: reads state.userLogin.userInfo.{following, followers,
 *            following_count, followers_count} (legacy canonical store).
 * Phase 6:   swaps internal reads to state.profile.data.{...} transparently.
 *            Public contract below is invariant across the swap.
 *
 * RETURN SHAPE (load-bearing — Phase 5 consumers depend on this):
 *
 *   {
 *     following:        Array<UserShort>      (default [] for null/undefined),
 *     followers:        Array<UserShort>      (default [] for null/undefined),
 *     following_count:  integer               (default 0  for null/undefined),
 *     followers_count:  integer               (default 0  for null/undefined),
 *   }
 *
 *   Keys are snake_case to match the backend payload so consumers may swap
 *   `userInfo.following` for `selectFollowGraph(state).following` without
 *   renaming downstream identifiers.
 *
 * MEMOIZATION CONTRACT (load-bearing — list-fanout consumers depend on this):
 *
 *   * Field-level input selectors (NOT a single userInfo input). The cache
 *     survives userInfo ref churn that doesn't touch a follow-graph field:
 *     token refresh, B2 USER_LOGIN_REQUEST/FAIL spreads, AUTH_IDENTITY_PATCH
 *     reconciliations, and any other userLogin mutation that does not write
 *     `following`, `followers`, `following_count`, or `followers_count` MUST
 *     NOT produce a new selector output reference.
 *
 *   * `following` / `followers` arrays are forwarded by reference. NO clone,
 *     NO spread, NO sort, NO dedup, NO remap. The selector is a projection
 *     seam, not a normalization layer (per SELECTOR_CONTRACTS.md).
 *
 *   * The default `[]` and `0` substitutions for null/undefined inputs are
 *     applied INSIDE the projector. createSelector caches the projector
 *     output, so the same `[]` literal produced on first run is the same
 *     reference returned on every subsequent run that observes the same
 *     null/undefined inputs.
 *
 * NO SEMANTIC TRANSFORMATION:
 *
 *   - Counts coalesce to literal 0, NOT to array length. Backend
 *     `UserSerializer.get_{following,followers}_count` is a separate DB
 *     `.count()` query and is conceptually independent of the embedded
 *     array length. Treat them as separate inputs.
 *   - No ordering. Backend iterates `obj.{follower,following}_links`
 *     without `order_by`; the selector preserves whatever order arrived.
 *   - No dedup. Backend `UserFollow` unique-together constraint guarantees
 *     uniqueness at write time; the selector trusts the wire.
 *
 * ⚠ SLICE-OWNERSHIP INVARIANT:
 *   Phase 4–5 MUST read from state.userLogin.userInfo. Per audit finding
 *   PH-2, state.profile.data.{following,followers} is already fresher than
 *   state.userLogin.userInfo.{following,followers} after bootstrapApp
 *   succeeds. Pointing this selector at the fresher source today would
 *   produce a behavioral change disguised as a no-op migration — consumers
 *   would observe data that the legacy reader never produced mid-session.
 *   The internal source swap is reserved for Phase 6, where the legacy
 *   slice becomes a derived projection of profile.data.
 *
 * ⚠ DO NOT compose selectFollowGraph into selectIdentity or vice versa.
 *   Per SELECTOR_CONTRACTS.md, identity selectors must NOT embed follow
 *   graph. They are separate ownership seams with separate Phase 6 swap
 *   timing.
 */
export const selectFollowGraph = createSelector(
  [
    (state) => state.userLogin?.userInfo?.following,
    (state) => state.userLogin?.userInfo?.followers,
    (state) => state.userLogin?.userInfo?.following_count,
    (state) => state.userLogin?.userInfo?.followers_count,
  ],
  (following, followers, followingCount, followersCount) => ({
    following: following ?? [],
    followers: followers ?? [],
    following_count: followingCount ?? 0,
    followers_count: followersCount ?? 0,
  })
);
